/**
 * Shared ProseMirror selection helper for placeholder insertion.
 *
 * Lifted out of `wiki-embed-placeholders.ts` so the card / comment embed
 * paths can use it too — it is generic PM plumbing, nothing wiki-specific.
 * That module re-exports it, so existing wiki importers are unaffected.
 */

import { NodeSelection, TextSelection, type Transaction } from '@tiptap/pm/state'

/**
 * Collapse a `NodeSelection` to a text caret AFTER the selected node.
 *
 * A `NodeSelection` means a whole node is selected, and `insertContent`
 * REPLACES the selection — so any insert made while one is active destroys
 * the selected node instead of adding beside it. Two distinct moments
 * produce one, and both need this:
 *
 *   - **After an insert.** `insertContent` leaves a just-inserted block
 *     atom node-selected, so the NEXT insert (the following file of a
 *     multi-file drop) would replace the placeholder just added.
 *   - **Before an insert.** Entering edit mode on a body whose FIRST node
 *     is a block atom resolves focus to a `NodeSelection` on it — no
 *     visible caret. A drop then replaced the existing image outright, and
 *     if the upload subsequently failed the placeholder was removed too,
 *     leaving the body with nothing. Guarding only after the insert (as
 *     the wiki path originally did) does not cover this.
 *
 * When the node sits at the end of its parent there is NO textblock after
 * it to hold a caret (the trailing-paragraph extension only appends via
 * appendTransaction, AFTER this chain dispatches — too late), so the
 * collapse creates the anchor itself: an empty paragraph right after the
 * node. A subsequent insert at that empty-paragraph caret replaces the
 * paragraph with the incoming node (TipTap's empty-textblock replacement),
 * so a batch never accumulates blank lines between placeholders.
 *
 * No-op when the selection is anything else, so it is safe to chain
 * unconditionally on both sides of an insert.
 */
export function collapseNodeSelectionAfter(tr: Transaction): void {
  const sel = tr.selection
  if (!(sel instanceof NodeSelection)) return
  const after = sel.to
  const near = TextSelection.near(tr.doc.resolve(after), 1)
  if (near instanceof TextSelection && near.from >= after) {
    tr.setSelection(near)
    return
  }
  const paragraph = tr.doc.type.schema.nodes.paragraph
  if (!paragraph) return
  tr.insert(after, paragraph.create())
  tr.setSelection(TextSelection.create(tr.doc, after + 1))
}
